import { useContext, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Checkbox,
  ClickAwayListener,
  Divider,
  MenuItem,
  Paper,
  Popper,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import "../../styles/settings.css";
import { GlobalContext } from "../../context/GlobalContext";
import IntegrationService from "../../services/integrations/Integrations";
import ProgressSpinner from "../Common/ProgressSpinner";
import { useQueryClient } from "@tanstack/react-query";

type TimePeriodValue =
  | "TODAY"
  | "TOMORROW"
  | "CURRENTWEEK"
  | "CURRENTMONTH"
  | "LASTWEEK"
  | "LASTMONTH"
  | "OVERDUE";

type ColumnValue =
  | "serialNo"
  | "title"
  | "account"
  | "assignee"
  | "coach"
  | "due_date"
  | "priority"
  | "status"
  | "execSponsor"
  | "tags"
  | "subscribers";

type TaskPreferenceState = {
  defaultTimePeriod: TimePeriodValue;
  defaultColumns: ColumnValue[];
};

const TIME_PERIOD_OPTIONS: { label: string; value: TimePeriodValue }[] = [
  { label: "Today", value: "TODAY" },
  { label: "Tomorrow", value: "TOMORROW" },
  { label: "Current Week", value: "CURRENTWEEK" },
  { label: "Current Month", value: "CURRENTMONTH" },
  { label: "Last Week", value: "LASTWEEK" },
  { label: "Last Month", value: "LASTMONTH" },
  { label: "Overdue", value: "OVERDUE" },
];

const COLUMN_OPTIONS: { label: string; value: ColumnValue }[] = [
  { label: "Serial No.", value: "serialNo"},
  { label: "Title*", value: "title" },
  { label: "Account", value: "account" },
  { label: "Solo Player*", value: "assignee" },
  { label: "Solo Coach", value: "coach" },
  { label: "Due Date*", value: "due_date" },
  { label: "Priority", value: "priority" },
  { label: "Status", value: "status" },
  { label: "Exec Sponsor", value: "execSponsor"},
  { label: "Tags", value: "tags"},
  { label: "Subscribers", value: "subscribers" },
];

const DEFAULT_PREFERENCES: TaskPreferenceState = {
  defaultTimePeriod: "CURRENTMONTH",
  defaultColumns: ["title", "assignee", "due_date"],
};

const LOCKED_COLUMNS: ColumnValue[] = [
  "title",
  "assignee",
  "due_date",
];

const areEqual = (a: TaskPreferenceState, b: TaskPreferenceState) =>
  a.defaultTimePeriod === b.defaultTimePeriod &&
  a.defaultColumns.length === b.defaultColumns.length &&
  a.defaultColumns.every((value, index) => value === b.defaultColumns[index]);

const mapApiToUI = (data: any): TaskPreferenceState => {
  const taskPrefs = data?.userPreferences?.table_preferences?.tasks;

  if (!taskPrefs) return DEFAULT_PREFERENCES;

  const columns = Object.entries(taskPrefs.columns || {})
    .filter(([_, value]) => value)
    .map(([key]) => key as ColumnValue);

  return {
    defaultTimePeriod: taskPrefs.default_time_period,
    defaultColumns: columns,
  };
};

const mapUIToApi = (preferences: TaskPreferenceState) => {
  const base: any = {
    title: false,
    status: false,
    assignee: false,
    due_date: false,
    priority: false,
    serialNo: false,
    coach: false,
    tags: false,
    subscribers: false,
    execSponsor: false,
    account: false,
  };

  preferences.defaultColumns.forEach((col) => {
    base[col] = true;
  });

  return {
    table_preferences: {
      tasks: {
        columns: base,
        default_time_period: preferences.defaultTimePeriod,
      },
    },
  };
};

const UserPreference = () => {
  const savedPreferences = DEFAULT_PREFERENCES;
  const [preferences, setPreferences] =
    useState<TaskPreferenceState>(savedPreferences);
  const [baselinePreferences, setBaselinePreferences] =
    useState<TaskPreferenceState>(savedPreferences);
  const [timePeriodOpen, setTimePeriodOpen] = useState(false);
  const timePeriodAnchorRef = useRef<HTMLButtonElement | null>(null);
  const { showSnackbar } = useDisplaySnackbar();
  const [showSpinner, setShowSpinner] = useState(false);
  const { accountDetails } = useContext(GlobalContext);
  const employeePorId = accountDetails?.profileOrgId ?? "";
  const queryClient = useQueryClient();

  const selectedTimePeriodLabel =
    TIME_PERIOD_OPTIONS.find(
      (option) => option.value === preferences.defaultTimePeriod,
    )?.label || "Current Month";

  const selectedColumns = COLUMN_OPTIONS.filter((option) =>
    preferences.defaultColumns.includes(option.value),
  );

  const hasChanges = !areEqual(preferences, baselinePreferences);

  const handleToggleColumn = (value: ColumnValue) => {
    if (LOCKED_COLUMNS.includes(value)) return;

    setPreferences((prev) => {
        const exists = prev.defaultColumns.includes(value);
        const nextColumns = exists
        ? prev.defaultColumns.filter((column) => column !== value)
        : [...prev.defaultColumns, value];

        return {
        ...prev,
        defaultColumns: nextColumns,
        };
    });
  };

  const { isLoading, isFetching, isSuccess, isError, error, data } = IntegrationService.useGetUserPreferences(
    { employeePorId },
    !!employeePorId
    );

  const { mutate: updatePreferences, isPending } = IntegrationService.useUpdateUserPreferences();

  useEffect(() => {
    if (isLoading || isFetching) {
        setShowSpinner(true);
    } else {
        setShowSpinner(false);
    }
    if (isSuccess && data) {
        const mapped = mapApiToUI(data);
        setPreferences(mapped);
        setBaselinePreferences(mapped);
    }
    if(isError) {
        showSnackbar("error", error?.message || "Failed to load data.");
    }   
  }, [data, isLoading, isSuccess, isFetching, isError, error]);

  const handleDeleteChip = (value: ColumnValue) => {
    if (LOCKED_COLUMNS.includes(value)) return;

    setPreferences((prev) => ({
        ...prev,
        defaultColumns: prev.defaultColumns.filter(
        (column) => column !== value
        ),
    }));
  };

  const handleSave = () => {
    const payload = mapUIToApi(preferences);

    updatePreferences(
        {
        params: { employeePorId },
        payload,
        },
        {
        onSuccess: () => {
            queryClient.setQueryData(
              ["get_user_preferences", { employeePorId }],
              (previousData: any) => ({
                ...(previousData || {}),
                userPreferences: {
                  ...(previousData?.userPreferences || {}),
                  ...payload,
                },
              })
            );
            queryClient.invalidateQueries({
              queryKey: ["get_user_preferences", { employeePorId }],
            });
            setBaselinePreferences(preferences);
            showSnackbar("success", "Preferences saved successfully");
        },
        onError: () => {
            showSnackbar("error", "Failed to save preferences");
        },
        }
    );
  };

  return (
    <Box className="container">
      <Box className="section-container">
        <Box sx={{ width: { xs: "100%", md: 230 } }}>
          <Typography className="section-header">
            Tasks - Default Time Period
          </Typography>
          <Typography className="section-sub-header">
            Choose the time range that will by default applied in the Tasks
            Page.
          </Typography>
        </Box>

        <ClickAwayListener onClickAway={() => setTimePeriodOpen(false)}>
          <Box sx={{ position: "relative", width: "142px" }}>
            <Button className="dropdown-button"
              ref={timePeriodAnchorRef}
              onClick={() => setTimePeriodOpen((prev) => !prev)}
              endIcon={<KeyboardArrowDownIcon sx={{ color: "#7B7B8F" }} />}
            >
              {selectedTimePeriodLabel}
            </Button>

            <Popper
              open={timePeriodOpen}
              anchorEl={timePeriodAnchorRef.current}
              placement="bottom-start"
              sx={{ zIndex: 1300, mt: 1 }}
            >
              <Paper className="time-period-dropdown"
                elevation={8}
              >
                {TIME_PERIOD_OPTIONS.map((option, index) => {
                  const selected =
                    preferences.defaultTimePeriod === option.value;
                  const isFirst = index === 0;
                  const isLast = index === TIME_PERIOD_OPTIONS.length - 1;

                  return (
                    <Box key={option.value}
                        sx={{
                            mt: isFirst ? "8px" : 0,
                            mb: isLast ? "8px" : 0,
                        }}
                    >
                      <MenuItem
                        onClick={() => {
                          setPreferences((prev) => ({
                            ...prev,
                            defaultTimePeriod: option.value,
                          }));
                          setTimePeriodOpen(false);
                        }}
                        className="time-period-dropdown-menu"
                      >
                        <span>{option.label}</span>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: selected
                              ? "6px solid #6036D6"
                              : "1.5px solid #7F8196",
                            backgroundColor: "#FFFFFF",
                          }}
                        />
                      </MenuItem>
                      {!isLast && <Divider sx={{ my: "8px" }} />}
                    </Box>
                  );
                })}
              </Paper>
            </Popper>
          </Box>
        </ClickAwayListener>
      </Box>

      <Box className="section-container">
        <Box sx={{ width: { xs: "100%", md: 230 } }}>
          <Typography className="section-header">
            Tasks - Default Columns
          </Typography>
          <Typography className="section-sub-header">
            Select which fields should appear by default in the Tasks Page.
          </Typography>
        </Box>

          <Box sx={{ position: "relative", width: { xs: "100%", md: 450 } }}>
            <Box className="flex-chip-container">
              {selectedColumns.map((column) => (
                <Chip
                  key={column.value}
                  label={column.label}
                  onDelete={
                    preferences.defaultColumns.length > 1
                      ? () => handleDeleteChip(column.value)
                      : undefined
                  }
                  deleteIcon={
                    <CloseIcon
                      sx={{ width: "16px", height: "16px", color: "#7A7A8C" }}
                    />
                  }
                  className="custom-chip"
                />
              ))}
            </Box>
            <Paper className="dropdown-paper"
                elevation={8}
            >
                {COLUMN_OPTIONS.map((option, index) => {
                  const isLocked = LOCKED_COLUMNS.includes(option.value);
                  const checked = isLocked || preferences.defaultColumns.includes(option.value);
                  const isFirst = index === 0;
                  const isLast = index === COLUMN_OPTIONS.length - 1;

                  return (
                    <Box key={option.value}
                        sx={{
                            mt: isFirst ? "8px" : 0,
                            mb: isLast ? "8px" : 0,
                        }}
                    >
                      <MenuItem
                        onClick={() => {
                          if (!isLocked) {
                            handleToggleColumn(option.value);
                          }
                        }}
                        className="columns-options"
                      >
                        <Checkbox
                          checked={checked}
                          disabled={isLocked}
                          sx={{
                            color: "#7A7A8C",
                            padding: "2px",
                            "& .MuiSvgIcon-root": {
                              fontSize: 18,
                            },
                            "&.Mui-checked": {
                              color: isLocked ? "#CEC1F2": "#6036D6",
                            },
                          }}
                        />
                        <span>{option.label}</span>
                      </MenuItem>
                       {!isLast && <Divider sx={{ my: "8px" }} />}
                    </Box>
                  );
                })}
            </Paper>
          </Box>
      </Box>

      <Box sx={{ pt: 2 }}>
        <Button
          className="save-button-user-preferences"
          onClick={handleSave}
          disabled={!hasChanges || isPending}
          sx={{
            backgroundColor: hasChanges ? "#6036D6" : "#E7E7EF",
            "&:hover": {
              backgroundColor: hasChanges ? "#5530C2" : "#E7E7EF",
            },
            "&.Mui-disabled": {
              color: "#A8A8BA",
            },
          }}
        >
          Save Preference
        </Button>
        {hasChanges && 
        <Button
          onClick={() => setPreferences(baselinePreferences)}
          sx={{
            minWidth: 100,
            height: 30,
            px: 2,
            borderRadius: "6px",
            textTransform: "none",
            fontSize: "14px",
            fontWeight: 500,
            color: "#9595A3",
          }}
        >
          Discard
        </Button>
        }
      </Box>
      <ProgressSpinner showSpinner={showSpinner} />
    </Box>
  );
};

export default UserPreference;
